import { useSyncedRef, useUnmountEffect, useUpdateEffect } from "@react-hookz/web";
import { useSetAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";

import { useBuildSpeechifyEventHandler } from "./hooks";

type Props = {
  inlinePlayerId: string;
};

export const ArticleSpeechPlayer: FunctionComponent<Props> = () => {
  const { articleSpeechifyPlayer, articleSpeechifyPlayerConfig, setArticleSpeechifyPlayer } =
    useArticleSpeechContext();
  const latestArticleSpeechifyPlayer = useSyncedRef(articleSpeechifyPlayer);
  const { speechifyEventHandler } = useBuildSpeechifyEventHandler(articleSpeechifyPlayer);
  const onePlayerDispatch = useSetAtom(onlyOnePlayingPlayerAtom);

  useUpdateEffect(() => {
    if (!articleSpeechifyPlayerConfig) return;
    const run = async () => {
      const speechifyWidget = await import(
        /* webpackIgnore: true */ "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs"
      );
      await latestArticleSpeechifyPlayer.current?.unmount();
      const widget = speechifyWidget.makeSpeechifyExperience(articleSpeechifyPlayerConfig);
      await widget.mount();
      setArticleSpeechifyPlayer?.(widget);
      onePlayerDispatch({
        payload: {
          articleSpeechifyPlayer: widget,
        },
        type: "loadArticleSpeechPlayer",
      });
    };
    void run();
  }, [
    latestArticleSpeechifyPlayer,
    onePlayerDispatch,
    setArticleSpeechifyPlayer,
    articleSpeechifyPlayerConfig,
  ]);

  useEffect(() => {
    if (!articleSpeechifyPlayer) return;
    const destructor = articleSpeechifyPlayer?.addEventListener(speechifyEventHandler);

    return () => {
      destructor();
    };
  }, [articleSpeechifyPlayer, speechifyEventHandler]);

  useUnmountEffect(() => {
    void articleSpeechifyPlayer?.unmount();
  });

  return null;
};
ArticleSpeechPlayer.displayName = "ArticleSpeechPlayer";
